import { IToolManifest } from '../../../core/tools/interfaces/ITool';
import { ConnectorTool } from '../../../core/tools/standard/ConnectorTool';
import { Edge, EdgeType } from '../../../core/models/scene';
import { getConnectorManifest, getRelationshipMapping, listConnectorManifests } from '../../../lib/diagram/registries';
import i18n from '@/i18n/config';
import { bindGenogramToolName } from './utils/toolNameI18n';

// Genogram-specific connector tool
export class GenogramConnectorTool extends ConnectorTool {
  manifest: IToolManifest;

  private edges: Edge[] = [];
  private addEdgeHandler: ((edge: Edge) => void) | null = null;
  private deleteEdgeHandler: ((id: string) => void) | null = null;
  private startAnchor: 'top' | 'right' | 'bottom' | 'left' | undefined;
  private endAnchor: 'top' | 'right' | 'bottom' | 'left' | undefined;
  private startOffset: number | undefined;
  private endOffset: number | undefined;
  private _diagramType: string = 'genogram';
  
  constructor() {
    super();
    
    // Build manifest from base and add genogram-specific properties
    this.manifest = {
      ...this.getBaseManifest(),
      id: 'genogram-connector',
      name: i18n.t('genogram:tools.connectPeople', { defaultValue: 'Connect People' }) as string,
      description: 'Create relationships between people',
      supportedDiagramTypes: ['genogram'],
    } as IToolManifest;
    bindGenogramToolName(this.manifest, 'tools.connectPeople', 'Connect People');
    
    // Set default connection type
    this.connectionType = 'line';
  }
  
  setEdgeHandlers(
    edges: Edge[],
    addEdge: (edge: Edge) => void,
    deleteEdge: (id: string) => void
  ): void {
    this.edges = edges;
    this.addEdgeHandler = addEdge;
    this.deleteEdgeHandler = deleteEdge;
  }
  
  setStartAnchor(anchor: 'top' | 'right' | 'bottom' | 'left' | null) {
    this.startAnchor = anchor || undefined;
  }
  setEndAnchor(anchor: 'top' | 'right' | 'bottom' | 'left' | null) {
    this.endAnchor = anchor || undefined;
  }
  setStartOffset(offset: number | null) {
    this.startOffset = offset === null || offset === undefined ? undefined : Math.max(0, Math.min(1, offset));
  }
  setEndOffset(offset: number | null) {
    this.endOffset = offset === null || offset === undefined ? undefined : Math.max(0, Math.min(1, offset));
  }

  setDiagramType(type: string) {
    this._diagramType = type;
  }

  getConnectionTypes(): { value: string; label: string; icon?: string }[] {
    const manifests = listConnectorManifests('genogram');
    return manifests.map((m) => ({ value: m.id, label: m.label || m.id, icon: undefined }));
  }
  
  createConnector(from: string, to: string, type: string): Edge {
    const diagramType = this._diagramType || (this.context?.diagramType as string) || 'genogram';
    let edgeType = type;
    let defaultAttrs: Record<string, unknown> = {};

    let fromAnchor = this.startAnchor;
    let toAnchor = this.endAnchor;
    let fromOffset = this.startOffset;
    let toOffset = this.endOffset;

    if (diagramType === 'flowchart') {
      const manifest = getConnectorManifest('flowchart', type);
      if (manifest) defaultAttrs = manifest.defaultProps();
    }

    if (diagramType === 'org' && type !== 'line') {
      const mapping = getRelationshipMapping('org', type);
      if (mapping) {
        edgeType = mapping.edgeType;
        defaultAttrs = mapping.defaultEdgeProps || {};
      }
    }
    if (diagramType === 'org' && edgeType === 'reporting') {
      fromAnchor = 'top';
      toAnchor = 'bottom';
      fromOffset = 0.5;
      toOffset = 0.5;
    }
    if (diagramType === 'org' && edgeType === 'occupies') {
      fromOffset = 0.5;
      toOffset = 0.5;
    }
    const edge: Edge = {
      id: `edge-${Date.now()}`,
      type: edgeType as EdgeType,
      from,
      to,
      // attach anchors for neutral line routing
      attrs: {
        ...defaultAttrs,
        fromAnchor,
        toAnchor,
        fromOffset,
        toOffset,
      },
    };
    // Back-compat: also set on the edge object for existing readers
    edge.fromAnchor = fromAnchor;
    edge.toAnchor = toAnchor;
    edge.fromOffset = fromOffset;
    edge.toOffset = toOffset;
    // Allow multiple connectors per anchor by not deduping on anchor pairs
    // Offsets left undefined here; can be set by UI later
    // reset for next use
    this.startAnchor = undefined;
    this.endAnchor = undefined;
    this.startOffset = undefined;
    this.endOffset = undefined;
    return edge;
  }
  
  connectorExists(from: string, to: string): boolean {
    return this.edges.some(e => 
      e.from === from && e.to === to
    );
  }
  
  addConnector(connector: Edge): void {
    if (this.addEdgeHandler) {
      this.addEdgeHandler(connector as Edge);
    }
  }
  
  deleteConnector(id: string): void {
    if (this.deleteEdgeHandler) {
      this.deleteEdgeHandler(id);
    }
  }
}
